<template>
  <v-row no-gutters>
    <v-col cols="8" offset="2">
      <v-toolbar dense :elevation="0">
        <v-toolbar-title class="text-primary-700">My Properties - Add Properties</v-toolbar-title>
      </v-toolbar>
      <v-stepper v-model="step" vertical>
        <v-stepper-step editable step="1" :complete="step > 1">
          Select Contact Type <strong v-if="contactTypeText">{{ contactTypeText }}</strong>
        </v-stepper-step>
        <v-stepper-content step="1">
          <v-btn color="primary" @click="() => selectContactType('landlord')">Landlord contact</v-btn>
          <v-btn class="ml-2" color="primary" @click="() => selectContactType('landlord_agent')">Landlords agent</v-btn>
        </v-stepper-content>

        <v-stepper-step editable step="2" :complete="step > 2">
          Select schemes to be added (optional) <strong v-if="selectedSchemes.length">({{ selectedSchemes.length }})</strong>
        </v-stepper-step>
        <v-stepper-content step="2">
          <v-row>
            <v-col>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn color="primary" @click="routeToAddScheme" v-on="on">
                    <v-icon>mdi-plus</v-icon>
                    Create Scheme
                  </v-btn>
                </template>
                <span>If the scheme does not appear in the list below, click here to add it to RPD</span>
              </v-tooltip>
            </v-col>
            <v-col>
              <v-autocomplete
                v-model="schemesLandlordFilter"
                :loading="schemeLandlords.loading"
                :items="schemeLandlords.items"
                :search-input.sync="schemeLandlords.search"
                cache-items
                flat
                hide-no-data
                clearable
                item-text="name"
                item-value="id"
                label="Landlord"
                @change="getSchemes"
              ></v-autocomplete>
            </v-col>
            <v-col>
              <v-text-field
                v-model="schemesSearch"
                append-icon="mdi-table-search"
                label="Search"
                single-line
                hide-details
                @input="getSchemes"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-data-table class="mt-4"
                        :headers="schemeHeaders"
                        :items="schemes.items"
                        :options.sync="schemes.pagination"
                        :loading="schemes.loading"
                        :server-items-length="schemes.totalRecords"
                        :items-per-page.sync="schemes.itemsPerPage"
                        :sort-by.sync="schemes.sortBy"
                        :sort-desc.sync="schemes.sortDesc"
                        :page.sync="schemes.page"
          >
            <template v-slot:body="{ items }">
              <tbody>
              <tr v-for="item in items" :key="item.id">
                <td><a @click="() => showEditScheme(item.id)">{{ item.name }}</a></td>
                <td>{{ item.address.data.full_address }}</td>
                <td class="text-center">{{ item.properties_count }}</td>
                <td>
                  <v-btn color="primary" @click="() => selectScheme(item)">
                    <v-icon>mdi-plus</v-icon>Add
                  </v-btn>
                </td>
              </tr>
              </tbody>
            </template>
          </v-data-table>
<!--          selected schemes list-->
          <v-row v-if="selectedSchemes.length">
            <v-col>
              <h3>Selected Schemes</h3>
              <v-list>
                <v-list-item v-for="scheme in selectedSchemes" :key="scheme.id">
                  <v-list-item-content>
                    <v-list-item-title>{{ scheme.name }}</v-list-item-title>
                    <v-list-item-subtitle>{{ scheme.address.data.full_address }}</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-btn icon @click="() => unSelectScheme(scheme)">
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>
          <v-btn color="primary" @click="step = 3">Continue</v-btn>
        </v-stepper-content>

        <v-stepper-step step="3" editable>
          Select standalone properties to be added (optional) <strong v-if="selectedProperties.length">({{ selectedProperties.length }})</strong>
        </v-stepper-step>
        <v-stepper-content step="3">
          <v-row>
            <v-col>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn color="primary" @click="routeToAddProperty" v-on="on">
                    <v-icon>mdi-plus</v-icon>
                    Create Property
                  </v-btn>
                </template>
                <span>If the property does not appear in the list below, click here to add it to RPD</span>
              </v-tooltip>
            </v-col>
            <v-col>
              <v-autocomplete
                v-model="propertiesLandlordFilter"
                :loading="propertyLandlords.loading"
                :items="propertyLandlords.items"
                :search-input.sync="propertyLandlords.search"
                cache-items
                flat
                hide-no-data
                clearable
                item-text="name"
                item-value="id"
                label="Landlord"
                @change="getProperties"
              ></v-autocomplete>
            </v-col>
            <v-col>
              <v-text-field
                v-model="propertiesSearch"
                append-icon="mdi-table-search"
                label="Search"
                single-line
                hide-details
                @input="getProperties"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-data-table class="mt-4"
                        :headers="propertyHeaders"
                        :items="properties.items"
                        :options.sync="properties.pagination"
                        :loading="properties.loading"
                        :server-items-length="properties.totalRecords"
                        :items-per-page.sync="properties.itemsPerPage"
                        :sort-by.sync="properties.sortBy"
                        :sort-desc.sync="properties.sortDesc"
                        :page.sync="properties.page"
          >
            <template v-slot:body="{ items }">
              <tbody>
              <tr v-for="item in items" :key="item.id">
                <td><a @click="() => showEditProperty(item.id)">{{ item.building_num }}</a></td>
                <td>{{ item.tenant.data.name }}</td>
                <td>{{ item.address.data.full_address }}</td>
                <td>
                  <v-btn color="primary" @click="() => selectProperty(item)">
                    <v-icon>mdi-plus</v-icon>Add
                  </v-btn>
                </td>
              </tr>
              </tbody>
            </template>
          </v-data-table>
<!--          selected properties list-->
          <v-row v-if="selectedProperties.length">
            <v-col>
              <h3>Selected Properties</h3>
              <v-list>
                <v-list-item v-for="property in selectedProperties" :key="property.id">
                  <v-list-item-content>
                    <v-list-item-title>{{ property.building_num? property.building_num + ',' : '' }} {{ property.tenant.data.name }}</v-list-item-title>
                    <v-list-item-subtitle>{{ property.address.data.full_address }}</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-btn icon @click="() => unSelectProperty(property)">
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>
          <h2>What is Standalone Property?</h2>
          <p>
            A standalone property is simply a property that is not included in a scheme. For example; a shop on the high street or a supermarket.
          </p>
          <v-btn :disabled="!isCanBeSubmitted" color="primary" @click="submit">Finish</v-btn>
        </v-stepper-content>
      </v-stepper>
    </v-col>
    <edit-scheme-dialog ref="editSchemeDialog"></edit-scheme-dialog>
    <edit-standalone-property-dialog ref="editStandalonePropertyDialog"></edit-standalone-property-dialog>
  </v-row>
</template>

<script>

import EditSchemeDialog from "@/components/schemes/editSchemeDialog.vue";
import EditStandalonePropertyDialog from "@/components/properties/editStandalonePropertyDialog.vue";

export default {
  components: {EditStandalonePropertyDialog, EditSchemeDialog},
  directives: {
  },
  data: () => ({
    step: 1,
    propertiesSearch: '',
    schemesSearch: '',
    propertiesLandlordFilter: null,
    schemesLandlordFilter: null,
    // landlords: {
    //   loading: false,
    //   items: [],
    //   search: '',
    // },
    schemeLandlords: {
      loading: false,
      items: [],
      search: '',
    },
    propertyLandlords: {
      loading: false,
      items: [],
      search: '',
    },
    schemes: {
      pagination: {},
      itemsPerPage: 10,
      sortBy: [],
      sortDesc: [],
      page: 1,
      totalRecords: 0,
      loading: false,
      items: [],
    },
    properties: {
      pagination: {},
      itemsPerPage: 10,
      sortBy: [],
      sortDesc: [],
      page: 1,
      totalRecords: 0,
      loading: false,
      items: [],
    },
    propertyHeaders: [
      { text: 'Unit', value: 'building_num' },
      { text: 'Tenant', value: 'tenant.data.name' },
      { text: 'Address', value: 'address' },
      { text: '', value: 'actions' },
    ],
    schemeHeaders: [
      { text: 'Name', value: 'name' },
      { text: 'Address', value: 'address' },
      { text: 'Properties', value: 'properties_count' },
      { text: '', value: 'actions' },
    ],
    selectedSchemes: [],
    selectedProperties: [],

    contactType: null,
  }),
  watch: {
    'schemeLandlords.search'() {
      this.getLandlords('scheme')
    },
    'propertyLandlords.search'() {
      this.getLandlords('property')
    },
    'schemesLandlordFilter'() {
      this.schemes.pagination.page = 1
    },
    'schemes.pagination': {
      handler() {
        this.getSchemes()
      },
      deep: true
    },
    'propertiesLandlordFilter'() {
      this.properties.pagination.page = 1
    },
    'properties.pagination': {
      handler() {
        this.getProperties()
      },
      deep: true
    },
  },
  computed: {
    contactTypeText() {
      switch (this.contactType) {
        case 'landlord':
          return 'Landlord contact'
        case 'landlord_agent':
          return 'Landlords agent'
        default:
          return ''
      }
    },
    isCanBeSubmitted() {
      return (this.selectedSchemes.length || this.selectedProperties.length) && this.contactType
    }
  },
  methods: {
    showEditProperty(id) {
      this.$refs.editStandalonePropertyDialog.showDialog(id);
    },
    showEditScheme(id) {
      this.$refs.editSchemeDialog.showDialog(id);
    },
    selectContactType(type) {
      this.contactType = type
      this.step = 2
    },
    async submit() {
      const formData = {
        contact_type: this.contactType,
        scheme_ids: this.selectedSchemes.map(s => s.id),
        property_ids: this.selectedProperties.map(p => p.id),
      };
      await this.$http.post(`/contacts/add-me`, formData)
      this.$router.push({name: 'my-properties'})
    },
    routeToAddScheme() {
      const routeData = this.$router.resolve({name: 'schemes.create'})
      window.open(routeData.href, '_blank');
    },
    routeToAddProperty() {
      const routeData = this.$router.resolve({name: 'properties.create'})
      window.open(routeData.href, '_blank');
    },
    async getSchemes() {
      this.schemes.loading = true;
      const response = await this.$http.get('/schemes', {
        params: {
          q: this.schemesSearch,
          include: 'address',
          landlord_id: this.schemesLandlordFilter,
          page: this.schemes.pagination.page,
          sort_by: this.schemes.pagination.sortBy.length ? this.schemes.pagination.sortBy[0] : null,
          desc: this.schemes.pagination.sortDesc.length ? (this.schemes.pagination.sortDesc[0] ? 1 : 0) : null,
          per_page: this.schemes.pagination.itemsPerPage,
          hide_ids: this.selectedSchemes.map(s => s.id),
        }
      });
      this.schemes.loading = false;
      const pagination = response.data.meta.pagination
      this.schemes.totalRecords = pagination.total
      this.schemes.items = response.data.data
    },
    async getProperties() {
      this.properties.loading = true;
      const response = await this.$http.get('/properties', {
        params: {
          q: this.propertiesSearch,
          include: 'address,tenant',
          landlord_id: this.propertiesLandlordFilter,
          page: this.properties.pagination.page,
          sort_by: this.properties.pagination.sortBy.length ? this.properties.pagination.sortBy[0] : null,
          desc: this.properties.pagination.sortDesc.length ? (this.properties.pagination.sortDesc[0] ? 1 : 0) : null,
          per_page: this.properties.pagination.itemsPerPage,
          hide_ids: this.selectedProperties.map(p => p.id),
          type: 'standalone',
        }
      });
      this.properties.loading = false;
      const pagination = response.data.meta.pagination
      this.properties.totalRecords = pagination.total
      this.properties.items = response.data.data
    },
    async getLandlords(type) {
      const response = await this.$http.get('/companies/', {
        params: {
          q: type === 'scheme' ? this.schemeLandlords.search : this.propertyLandlords.search,
          per_page: 100,
        }
      });

      if (type === 'scheme') {
        this.schemeLandlords.items = response.data.data
      } else {
        this.propertyLandlords.items = response.data.data
      }
    },
    selectScheme(scheme) {
      this.selectedSchemes.push(scheme)
      this.schemes.items = this.schemes.items.filter(s => s.id !== scheme.id)
    },
    unSelectScheme(scheme) {
      this.selectedSchemes = this.selectedSchemes.filter(s => s.id !== scheme.id)
      this.schemes.items.push(scheme)
    },
    selectProperty(property) {
      this.selectedProperties.push(property)
      this.properties.items = this.properties.items.filter(p => p.id !== property.id)
    },
    unSelectProperty(property) {
      this.selectedProperties = this.selectedProperties.filter(p => p.id !== property.id)
      this.properties.items.push(property)
    },
  }
}
</script>

<style scoped>

</style>
